import { useEffect } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import * as Yup from 'yup'
import { Formik, FormikHelpers } from 'formik'
import { Button as MuiButton, Grid, Stack, TextField as MuiTextField, Box } from '@mui/material'
import { spacing } from '@mui/system'
import { styled } from '@mui/material/styles'
import { RootState } from '../redux'
import { createLevel, updateLevel, hideLevel } from '../redux/slices/levels'
import { getProjects } from '../redux/slices/projects'
import useAuth from '../hooks/useAuth'
import { useTranslation } from 'react-i18next'
import useLevel from '../hooks/useLevel'
import ProjectSelect from './ProjectSelect'
import levelService from '../services/levelService'
import { deleteError, setError } from '../redux/slices/errors'
import { LevelErrors } from '../enums/LevelErrors'
import { createErrorOrSuccessNotification } from '../redux/slices/notifications'
import { NotificationType } from '../enums/NotificationType'
import { createUserNotification } from '../utils/createUserNotification'
import { Level, LevelInput } from '../types'
import { LoadingButton } from '@mui/lab'

const Button = styled(MuiButton)(spacing)
const TextField = styled(MuiTextField)<{ my?: number }>(spacing)
export interface LevelFormProps {
  value?: LevelInput
  onCancel?: () => void
  onSubmit?: (values: LevelInput, helpers: FormikHelpers<LevelInput>) => Promise<void>
  beforeSubmit?: (values: LevelInput, helpers: FormikHelpers<LevelInput>) => LevelInput
  afterSubmit?: (level: Level) => void
  hidden?: Array<keyof LevelInput>
  disabled?: Array<keyof LevelInput>
}

export default function LevelForm({
  value,
  hidden = ['project' as keyof LevelInput],
  disabled = [],
  onCancel,
  onSubmit,
  beforeSubmit,
  afterSubmit
}: LevelFormProps) {
  /**
   * The dispatch function.
   */
  const dispatch = useDispatch()

  /**
   * The translate function.
   */
  const [t] = useTranslation('common')

  /**
   * The current user.
   */
  const { currentUser } = useAuth()

  /**
   * The level.
   */
  const level = useLevel()

  /**
   * Indicates if the projects are being loaded.
   */
  const isLoadingProjects = useSelector((state: RootState) => state.projects.loading)

  const initialValues: LevelInput =
    level === undefined
      ? {
          name: '',
          number: null,
          project: currentUser?.activeProjectMembership?.projectId ?? null,
          ...value
        }
      : {
          name: level.name,
          number: level.number,
          project: level.project?.id ?? null,
          ...value
        }

  const validationSchema = Yup.object().shape({
    // FIXME: Add unique condition:
    // - Name should be unique among levels of the selected project
    name: Yup.string().required(
      t('isRequired', {
        field: t('Level.name').toLocaleLowerCase()
      })
    ),
    // FIXME: Add unique condition:
    // - Number should be unique among levels of the selected project
    number: Yup.number()
      .typeError(
        t('isNumber', {
          field: t('Level.number').toLocaleLowerCase()
        })
      )
      .required(
        t('isRequired', {
          field: t('Level.number').toLocaleLowerCase()
        })
      ),
    project: Yup.number().required(
      t('isRequired', {
        field: t('Level.project').toLocaleLowerCase()
      })
    )
  })

  function isHidden(field: keyof LevelInput): boolean {
    return hidden.includes(field)
  }

  function isDisabled(field: keyof LevelInput): boolean {
    return disabled.includes(field)
  }

  useEffect(() => {
    dispatch(getProjects())
  }, [])

  async function handleSubmit(
    values: LevelInput,
    { resetForm, setErrors, setStatus, setSubmitting, ...helpers }: FormikHelpers<LevelInput>
  ) {
    try {
      if (onSubmit) {
        return await onSubmit(values, {
          resetForm,
          setErrors,
          setStatus,
          setSubmitting,
          ...helpers
        })
      } else {
        if (beforeSubmit) {
          beforeSubmit(values, {
            resetForm,
            setErrors,
            setStatus,
            setSubmitting,
            ...helpers
          })
        }

        setSubmitting(true)

        let createdOrUpdatedLevel: Level

        if (level === undefined) {
          const createdLevel: Level = await levelService.createLevel(values)

          dispatch(createLevel(createdLevel))
          dispatch(
            createErrorOrSuccessNotification(NotificationType.SUCCESS, t('levelCreateSuccess'))
          )

          createdOrUpdatedLevel = createdLevel
        } else {
          const updatedLevel: Level = await levelService.updateLevel(level.id, values)

          dispatch(updateLevel(updatedLevel))
          dispatch(
            createErrorOrSuccessNotification(NotificationType.SUCCESS, t('levelUpdateSuccess'))
          )

          createdOrUpdatedLevel = updatedLevel
        }

        resetForm()
        setStatus({ sent: true })
        dispatch(hideLevel())

        if (afterSubmit) {
          afterSubmit(createdOrUpdatedLevel)
        }
      }
    } catch (error) {
      setStatus({ sent: false })
      // FIXME: Set validation errors.
      dispatch(
        setError({
          type: level ? LevelErrors.EDIT : LevelErrors.CREATE,
          error: error
        })
      )

      const errorMessage = createUserNotification({
        user: currentUser,
        type: level ? LevelErrors.EDIT : LevelErrors.CREATE,
        error: error
      })

      dispatch(
        createErrorOrSuccessNotification(
          NotificationType.WARNING,
          t(errorMessage.key) + t(errorMessage.message)
        )
      )
      //TODO: Do not delete errors until submitting form has passed if error is validation error from the backend
      dispatch(deleteError(level ? LevelErrors.EDIT : LevelErrors.CREATE))
    } finally {
      setSubmitting(false)
    }
  }

  function handleCancel() {
    if (onCancel) {
      onCancel()
    } else {
      dispatch(hideLevel())
    }
  }

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      enableReinitialize
      onSubmit={handleSubmit}
    >
      {({ values, errors, touched, isSubmitting, handleBlur, handleChange, handleSubmit }) => (
        <Box mt={2}>
          <form onSubmit={handleSubmit}>
            <Grid container spacing={6}>
              {!isHidden('project') && (
                <Grid item xs={12}>
                  <ProjectSelect
                    data-testid="project"
                    name="project"
                    label={t('Level.project')}
                    value={values.project}
                    error={Boolean(touched.project && errors.project)}
                    helperText={touched.project && errors.project}
                    disabled={isDisabled('project') || isLoadingProjects || isSubmitting}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
              )}

              {!isHidden('number') && (
                <Grid item xs={12}>
                  <TextField
                    type="text"
                    name="number"
                    label={t('Level.number')}
                    value={values.number}
                    error={Boolean(touched.number && errors.number)}
                    helperText={touched.number && errors.number}
                    disabled={isDisabled('number') || isSubmitting}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    variant="outlined"
                    fullWidth
                  />
                </Grid>
              )}

              {!isHidden('name') && (
                <Grid item xs={12}>
                  <TextField
                    name="name"
                    label={t('Level.name')}
                    value={values.name}
                    error={Boolean(touched.name && errors.name)}
                    helperText={touched.name && errors.name}
                    disabled={isDisabled('name') || isSubmitting}
                    onBlur={handleBlur}
                    onChange={handleChange}
                    fullWidth
                    variant="outlined"
                  />
                </Grid>
              )}

              <Grid item xs={12}>
                <Stack direction="row" alignItems="center" justifyContent="flex-end" spacing={2}>
                  <Button type="button" disabled={isSubmitting} onClick={handleCancel}>
                    {t('cancel')}
                  </Button>

                  <LoadingButton
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isLoadingProjects}
                    loading={isSubmitting}
                  >
                    {t('save')}
                  </LoadingButton>
                </Stack>
              </Grid>
            </Grid>
          </form>
        </Box>
      )}
    </Formik>
  )
}
